/**
 * lumen-range.js
 * Custom element <lumen-range> — dual-handle range slider for discrete
 * list-type filters grouped by unit (lm, lm/m, W, W/m, K, …).
 *
 * Multiple <lumen-range> elements can exist inside one filter panel,
 * one per unit group. Each is fully independent.
 *
 * data-values: comma-separated entries "label|paramName|count|active"
 * data-unit:   unit label (informational, used for aria)
 */

class LumenRange extends HTMLElement {
  connectedCallback() {
    const raw = this.dataset.values || '';
    this.values = raw.split(',').filter(Boolean).map((chunk) => {
      const parts = chunk.split('|');
      return {
        label:     parts[0] || '',
        paramName: parts[1] || '',
        count:     parseInt(parts[2], 10) || 0,
        active:    parts[3] === 'true',
      };
    });

    if (this.values.length < 2) return;

    this.minIndex = 0;
    this.maxIndex = this.values.length - 1;

    // Restore active state from data attributes or pre-checked values
    const activeValues = this.values
      .map((v, i) => v.active ? i : -1)
      .filter(i => i !== -1);
    if (activeValues.length >= 1) {
      this.minIndex = activeValues[0];
      this.maxIndex = activeValues[activeValues.length - 1];
    }

    this.sliderMin  = this.querySelector('.lumen-range__slider-min');
    this.sliderMax  = this.querySelector('.lumen-range__slider-max');
    this.inputMin   = this.querySelector('.lumen-range__input-min');
    this.inputMax   = this.querySelector('.lumen-range__input-max');
    this.hiddenWrap = this.querySelector('.lumen-range__hidden-inputs');

    if (!this.sliderMin || !this.sliderMax || !this.inputMin || !this.inputMax) return;

    const maxIdx = this.values.length - 1;
    this.sliderMin.min = 0; this.sliderMin.max = maxIdx; this.sliderMin.step = 1;
    this.sliderMax.min = 0; this.sliderMax.max = maxIdx; this.sliderMax.step = 1;
    this.sliderMin.value = this.minIndex;
    this.sliderMax.value = this.maxIndex;

    this._updateInputs();
    this._updateTrack();

    // Re-materialise the hidden inputs for the restored (active) range.
    // facet-filters.js rebuilds the whole #facets form on every apply, which
    // recreates this element with an EMPTY hidden-inputs wrapper. Without this
    // call the range's parameters would be missing from the next
    // `new FormData(form)`, so the filter gets dropped as soon as any other
    // filter changes. We sync WITHOUT dispatching an event (no apply loop).
    this._syncHidden();

    this.sliderMin.addEventListener('input', () => this._onSliderMin());
    this.sliderMax.addEventListener('input', () => this._onSliderMax());
    this.inputMin.addEventListener('change', () => this._onTextMin());
    this.inputMax.addEventListener('change', () => this._onTextMax());
  }

  _onSliderMin() {
    let v = parseInt(this.sliderMin.value, 10);
    if (v > this.maxIndex) { v = this.maxIndex; this.sliderMin.value = v; }
    this.minIndex = v;
    this._updateInputs(); this._updateTrack(); this._commit();
  }

  _onSliderMax() {
    let v = parseInt(this.sliderMax.value, 10);
    if (v < this.minIndex) { v = this.minIndex; this.sliderMax.value = v; }
    this.maxIndex = v;
    this._updateInputs(); this._updateTrack(); this._commit();
  }

  _onTextMin() {
    const idx = this._closest(this.inputMin.value);
    this.minIndex = Math.min(idx, this.maxIndex);
    this.sliderMin.value = this.minIndex;
    this._updateInputs(); this._updateTrack(); this._commit();
  }

  _onTextMax() {
    const idx = this._closest(this.inputMax.value);
    this.maxIndex = Math.max(idx, this.minIndex);
    this.sliderMax.value = this.maxIndex;
    this._updateInputs(); this._updateTrack(); this._commit();
  }

  _closest(text) {
    const exact = this.values.findIndex(v => v.label.toLowerCase() === text.toLowerCase());
    if (exact !== -1) return exact;
    const num = parseFloat(text.replace(/[^\d.]/g, ''));
    if (isNaN(num)) return 0;
    let best = 0, bestDiff = Infinity;
    this.values.forEach((v, i) => {
      const n = parseFloat(v.label.replace(/[^\d.]/g, ''));
      if (!isNaN(n) && Math.abs(n - num) < bestDiff) { bestDiff = Math.abs(n - num); best = i; }
    });
    return best;
  }

  _updateInputs() {
    this.inputMin.value = this.values[this.minIndex]?.label ?? '';
    this.inputMax.value = this.values[this.maxIndex]?.label ?? '';
  }

  _updateTrack() {
    const max = this.values.length - 1;
    if (max === 0) return;
    const pMin = (this.minIndex / max) * 100;
    const pMax = (this.maxIndex / max) * 100;
    const sliders = this.querySelector('.price-range__sliders');
    if (sliders) {
      sliders.style.setProperty('--range-min', `${pMin}%`);
      sliders.style.setProperty('--range-max', `${pMax}%`);
    }
  }

  _syncHidden() {
    // Remove previously injected hidden inputs
    this.hiddenWrap.querySelectorAll('[data-lr]').forEach(el => el.remove());

    // Full range = no filter applied
    if (this.minIndex === 0 && this.maxIndex === this.values.length - 1) return;

    // Inject one hidden input per value in the selected range
    for (let i = this.minIndex; i <= this.maxIndex; i++) {
      const v = this.values[i];
      const inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = v.paramName;
      inp.value = v.label;
      inp.dataset.lr = '1';
      this.hiddenWrap.appendChild(inp);
    }
  }

  _commit() {
    this._syncHidden();

    // Notify facet-filters.js to re-fetch. We dispatch an 'input' event from
    // this element (no id) so it passes the handler's guard — a 'change' event
    // from the wrapper was being dropped, which is why text-input edits and the
    // slider sometimes failed to reload.
    requestAnimationFrame(() => {
      this.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }
}

if (!customElements.get('lumen-range')) {
  customElements.define('lumen-range', LumenRange);
}
